import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles, Fade } from '@material-ui/core';
import { CloudQueue, CloudOff } from '@material-ui/icons';

import { NetworkStatusValuesContext } from 'contexts';
import Message from 'components/Message';

const useStyles = makeStyles((theme) => ({
  alert: ({ alertBannerLeft }) => ({
    position: 'absolute',
    top: theme.spacing(11),
    left: alertBannerLeft,
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    width: `calc(100vw - 48px - ${alertBannerLeft}px)`,
    zIndex: 2,
  }),
}));

const getCurrentStatusData = (isOffline) => {
  if (isOffline) {
    return {
      icon: <CloudOff fontSize="inherit" />,
      text: `You're currently reading offline. While offline, some features are not available. Any highlights, notes, bookmarks, or flashcards you create or edit while offline will sync when your internet connection is back.`,
    };
  }

  return {
    icon: <CloudQueue fontSize="inherit" />,
    text: `You're back online. Any highlights, notes, bookmarks, or flashcards created offline will now sync.`,
  };
};

const fadeComponentOptions = {
  enter: 400,
  exit: 400,
};

const NetworkStatusAlert = ({
  areLearningToolsOpen,
  learningToolsMenuName,
  messageRef,
  ...props
}) => {
  const { isOffline } = useContext(NetworkStatusValuesContext);

  let learningToolsWidth = 0;
  if (areLearningToolsOpen && Boolean(learningToolsMenuName)) {
    learningToolsWidth = 327; // drawer + menu
  } else if (areLearningToolsOpen) {
    learningToolsWidth = 71; // drawer
  }

  const classes = useStyles({
    alertBannerLeft: learningToolsWidth,
  });

  const [shouldShowAlert, setShouldShowAlert] = useState(false);
  const [canShowOnlineAlert, setCanShowOnlineAlert] = useState(false);

  useEffect(() => {
    if (isOffline) {
      setShouldShowAlert(true);
      setCanShowOnlineAlert(true);
    }

    if (canShowOnlineAlert && !isOffline) {
      setShouldShowAlert(true);
    }
  }, [isOffline, canShowOnlineAlert]);

  const handleCloseClick = () => {
    setShouldShowAlert(false);
  };

  const { icon, text } = getCurrentStatusData(isOffline);

  // we need to remove it from the DOM so the SR can announce when it mounts
  return (
    <Fade
      in={shouldShowAlert}
      timeout={fadeComponentOptions}
      mountOnEnter
      unmountOnExit
    >
      <Message
        className={classes.alert}
        text={text}
        icon={icon}
        shouldHaveAction={true}
        handleCloseClick={handleCloseClick}
        messageRef={messageRef}
        {...props}
      />
    </Fade>
  );
};

NetworkStatusAlert.propTypes = {
  className: PropTypes.string,
  messageRef: PropTypes.object,
};

export default NetworkStatusAlert;
